import folderCog from "@iconify/icons-mdi/folder-cog";
import { Icon } from "@iconify/react";
import { Archive, BarChart, Delete, Edit, Schedule } from "@material-ui/icons";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { FoldersApi } from "../../../api/folders/folders";
import SectionedPopupMenu from "../../../components/Menus/PopupMenu/SectionedPopupMenu";
import { Permissions } from "../../../data/Permissions";
import { useSnackbar } from "../../../data/SnackbarContext";
import { ARCHIVE_FOLDER_DIALOG, DELETE_FOLDER_DIALOG, EDIT_FOLDER_FORM, FOLDER_SETTINGS_MENU, SNACKBAR_MESSAGES } from "../../../data/StringConstants";
import CustomButton from "../../CustomButton/CustomButton";
import CustomConfirmationDialog from "../../CustomConfirmationDialog/CustomConfirmationDialog";
import ArchiveFolderDialogActions from "../../Dialogs/DialogActions/ArchiveFolderDialogActions";
import DeleteFolderDialogActions from "../../Dialogs/DialogActions/DeleteFolderDialogActions";
import EditFolderForm from "../../Forms/EditFolderForm";

import analytics from "../../../utils/analytics";

/**
 * An instance of the SectionedPopupMenu component used for a menu of actions that update the settings of a specific folder.
 */
export default function FolderSettingsMenu({ folderDetails, migrateParentState }) {
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const _defaultDialogState = {
		show: false,
		action: false,
		title: "",
		body: "",
		actions: ""
	};
	const [optionDialog, setOptionDialog] = useState(_defaultDialogState);
	const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();

	const handleClick = event => setAnchorEl(event.currentTarget);
	const handleClose = (label) => {
		analytics.logEvent(analytics.eventTypes.FOLDERS.FOLDER_SETTINGS_CLICKED, {
				folder: {
					id: folderDetails.folderId,
					name: folderDetails.name
				},
				folderSettingsMenuItem: label
			});
		setAnchorEl(null);
	};
	const getReportUrl = () => `#!/folders/${folderDetails.folderId}/promotional-report`;
	const getScheduleUrl = () => `#!/folders/${folderDetails.folderId}/publication`;

	const folderSettingsActions = {
		primaryActions: [
			{
				label: FOLDER_SETTINGS_MENU.FOLDER_EDIT,
				icon: <Edit />,
				permissions: [Permissions.editor],
				action: () => {
					setOptionDialog({ ...optionDialog, action: "edit"})
				},
				noNewTab: true
			},
			{
				label: FOLDER_SETTINGS_MENU.FOLDER_SCHEDULE,
				icon: <Schedule />,
				permissions: [Permissions.viewer],
				target: getScheduleUrl(),
				noNewTab: true
			},
			{
				label: FOLDER_SETTINGS_MENU.FOLDER_REPORT,
				icon: <BarChart />,
				permissions: [Permissions.viewer],
				target: getReportUrl(),
				noNewTab: true
			},
			{
				label: FOLDER_SETTINGS_MENU.FOLDER_ARCHIVE,
				icon: <Archive />,
				permissions: [Permissions.editor],
				action: () => {
					setOptionDialog({ ...optionDialog, action: "archive"})
				},
				noNewTab: true
			},
		],
		destructiveActions: [
			{
				destructive: true,
				label: FOLDER_SETTINGS_MENU.FOLDER_DELETE,
				icon: <Delete />,
				permissions: [Permissions.globalSupport],
				action: () => {
					setOptionDialog({ ...optionDialog, action: "delete"})
				},
			}
		]
	};

	/**
	 * @todo - move this out into an actions component. requires 'create' codeflow updates as well
	 * @param {*} data 
	 */
	const onEditFolderDetailsSubmit = async data => {
		data.folderId = folderDetails.folderId;
		try {
			await FoldersApi.updateFolder(data);
			migrateParentState(prevData => {
				return {
					...prevData,
					name: data.name,
					description: data.description,
					defaultBrandingSetId: data.brandingSet
				};
			});
			showSuccessSnackbar(SNACKBAR_MESSAGES.UPDATE_FOLDER_DETAILS_SUCCESS);
		}
		catch (err) {
			showErrorSnackbar(SNACKBAR_MESSAGES.UPDATE_FOLDER_DETAILS_ERROR);
		}
	};

	const resetOptions = () => {
		setOptionDialog(_defaultDialogState);
	}

	const goToFolders = () => {
		resetOptions();
		history.push("#!/folders");
	}

	useEffect(() => {
		if (!optionDialog.show) {
			switch (optionDialog.action) {
				case "archive": setOptionDialog({
					...optionDialog,
					show: true,
					title: ARCHIVE_FOLDER_DIALOG.TITLE,
					body: ARCHIVE_FOLDER_DIALOG.BODY,
					dialogActions: <ArchiveFolderDialogActions folderId={folderDetails.folderId} folderName={folderDetails.name} toggle={resetOptions} callback={goToFolders} />
				});
					break;
				case "edit": setOptionDialog({
					...optionDialog,
					show: true,
					title: EDIT_FOLDER_FORM.TITLE,
					body: <EditFolderForm
						data={folderDetails}
						onCancel={resetOptions}
						onSubmit={onEditFolderDetailsSubmit}
						isCreate={false} />
				});
					break;
				case "delete": setOptionDialog({
					...optionDialog,
					show: true,
					title: DELETE_FOLDER_DIALOG.TITLE,
					body: DELETE_FOLDER_DIALOG.BODY,
					dialogActions: <DeleteFolderDialogActions folderId={folderDetails.folderId} folderName={folderDetails.name} toggle={resetOptions} callback={goToFolders} />
				});
					break;
			}
		}
	}, [optionDialog]);

	return (
		<Fragment>
			<SectionedPopupMenu
				menuItemSections={[
					folderSettingsActions.primaryActions,
					folderSettingsActions.destructiveActions
				]}
				triggerButton={() => (
					<CustomButton
						color="primary"
						onClick={handleClick}
						startIcon={<Icon icon={folderCog} />}
					>
						{FOLDER_SETTINGS_MENU.LABEL}
					</CustomButton>
				)}
				handleClose={handleClose}
				anchorEl={anchorEl}
			/>
			<CustomConfirmationDialog
				actions={optionDialog.dialogActions}
				onClose={resetOptions}
				open={optionDialog.show}
				title={optionDialog.title}
			>
				{optionDialog.body}
			</CustomConfirmationDialog>
		</Fragment>
	);
}

FolderSettingsMenu.propTypes = {
	folderDetails: PropTypes.shape({
		isLoading: PropTypes.bool,
		name: PropTypes.string,
		description: PropTypes.string,
		correlationId: PropTypes.string,
		audience: PropTypes.object,
		defaultBrandingSetId: PropTypes.number,
		brandingSets: PropTypes.array,
		folderId: PropTypes.number
	}).isRequired,
	migrateParentState: PropTypes.func.isRequired
};
